// Streaming-safe content formatting, ported from HemIQ's Message.jsx
// (orchid-cortex). Everything here is pure so it is unit-testable; AiMessage
// is the only consumer.
import { CHART_FENCE } from './sseProtocol';

// Strip a trailing, unterminated HTML comment (e.g. a half-streamed
// "<!--title:Ava"), including bare partial openers "<", "<!", "<!-".
// HemIQ never needed this (it didn't token-stream); we do.
export const stripPartialTitleComment = (text: string): string => {
  const lastOpen = text.lastIndexOf('<!--');
  if (lastOpen !== -1 && text.indexOf('-->', lastOpen) === -1) {
    return text.slice(0, lastOpen);
  }
  const partialOpener = /(?:<|<!|<!-)$/.exec(text);
  return partialOpener ? text.slice(0, partialOpener.index) : text;
};

// Returns the substring up to (not including) the last unclosed ``` opener,
// or null when all code blocks are closed. Used to safely render partial
// markdown during streaming without passing broken fences to ReactMarkdown.
export const getStreamingPrefix = (content: string): string | null => {
  const parts = content.split('```');
  if (parts.length % 2 === 0) {
    return content.slice(0, content.lastIndexOf('```'));
  }
  return null;
};

// Language identifier of the currently-open (unclosed) code fence.
export const getOpenBlockLanguage = (content: string): string => {
  const lastIndex = content.lastIndexOf('```');
  if (lastIndex === -1) return '';
  return content
    .slice(lastIndex + 3)
    .split('\n')[0]
    .trim()
    .toLowerCase();
};

const CODE_LANGS = new Set([
  'javascript', 'js', 'typescript', 'ts', 'jsx', 'tsx', 'python', 'py', 'json',
  'sql', 'bash', 'sh', 'html', 'css', 'yaml', 'yml', 'xml', 'markdown', 'md',
]);

export const getBlockLabel = (language: string): string => {
  const lang = (language || '').toLowerCase();
  if (lang.includes(CHART_FENCE)) return 'Generating visualization…';
  if (lang === 'json') return 'Generating data…';
  if (CODE_LANGS.has(lang)) return 'Generating code…';
  return 'Writing…';
};

export type ContentSegment =
  | { type: 'text'; content: string; key: string }
  | { type: 'chart'; rawValue: string; key: string };

// Split fully-closed ```stackiq-chart fences out of the content so they can
// render as stable memoized components (charts don't re-parse on every
// streaming delta). Keys are character positions so they stay stable while
// trailing text streams in.
export const splitContentSegments = (content: string): ContentSegment[] => {
  const segments: ContentSegment[] = [];
  const fenceRe = new RegExp('```' + CHART_FENCE + '\\r?\\n([\\s\\S]*?)```', 'g');
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = fenceRe.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', content: content.slice(lastIndex, match.index), key: `t${lastIndex}` });
    }
    segments.push({ type: 'chart', rawValue: match[1].replace(/\n$/, ''), key: `c${match.index}` });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    segments.push({ type: 'text', content: content.slice(lastIndex), key: `t${lastIndex}` });
  }
  return segments;
};

// The full streaming pipeline for an in-flight assistant message:
// completed title comments are peeled by the caller (extractTitle); this
// handles the partial tail + unclosed fences.
export const prepareStreamingContent = (
  body: string
): { renderable: string; openBlockLabel: string | null } => {
  const withoutPartialComment = stripPartialTitleComment(body);
  const prefix = getStreamingPrefix(withoutPartialComment);
  if (prefix === null) {
    return { renderable: withoutPartialComment, openBlockLabel: null };
  }
  return {
    renderable: prefix,
    openBlockLabel: getBlockLabel(getOpenBlockLanguage(withoutPartialComment)),
  };
};
